import {Employee, EmployeeRef, KeyLocation} from "../../api/Model";
import VaultIcon from "@mui/icons-material/AccountBalance";
import EmployeeIcon from "@mui/icons-material/Person";
import ContractorIcon from '@mui/icons-material/Engineering';
import DamagedIcon from '@mui/icons-material/KeyOff';
import LostIcon from '@mui/icons-material/PsychologyAlt';
import DeactivatedIcon from '@mui/icons-material/DoDisturbOn';
import React from "react";
import {Grid, GridProps, SvgIconTypeMap, Typography} from "@mui/material";
import {OverridableComponent} from "@mui/material/OverridableComponent";
import EmployeeAvatar from "../employee-avatar/EmployeeAvatar";
import EmployeeLink from "../employee-link/EmployeeLink";
import {grey} from "@mui/material/colors";
import {emptyEmployee} from "../../api/Empty";

export type Location =
    'with-vault'
    | 'with-employee'
    | 'with-guest'
    | 'lost'
    | 'damaged'
    | 'deactivated';


export const locationTypeOptions: Record<Location, {
    label: string,
    emoji: string,
    icon: OverridableComponent<SvgIconTypeMap>
}> = {
    'with-vault': {
        label: 'In Vault',
        emoji: '🔑',
        icon: VaultIcon,
    },
    'with-employee': {
        label: 'With Employee',
        emoji: '🧑',
        icon: EmployeeIcon,
    },
    'with-guest': {
        label: 'With Contractor',
        emoji: '👷',
        icon: ContractorIcon
    },
    'lost': {
        label: 'Lost',
        emoji: '🔎',
        icon: LostIcon,
    },
    'damaged': {
        label: 'Damaged',
        emoji: '😵',
        icon: DamagedIcon,
    },
    'deactivated': {
        label: 'Deactivated',
        emoji: '❌',
        icon: DeactivatedIcon,
    },
};

export function toLocationTypeEnum(location: KeyLocation) {
    let locationDetails: {
        locationType: Location,
        employee?: Employee,
        guestFullName?: string,
        guestCompany?: string,
        expectedReturnDate?: string,
    }

    if (location.type === 'with-employee') {
        locationDetails = {
            locationType: 'with-employee',
            employee: {...emptyEmployee, ...location.employee},
            expectedReturnDate: location.expTo,
        }
    } else if (location.type === 'with-guest') {
        locationDetails = {
            locationType: 'with-guest',
            guestFullName: location.fullName,
            guestCompany: location.company,
            expectedReturnDate: location.expTo,
        }
    } else {
        locationDetails = {
            locationType: location.type
        }
    }

    return locationDetails
}

export function fromLocationTypeEnum(
    locationType: Location,
    employee?: EmployeeRef,
    guestFullName?: string,
    guestCompany?: string,
    expTo?: string,
): KeyLocation | null {
    let location: KeyLocation | null = null;
    switch (locationType) {
        case 'with-vault':
            location = {type: 'with-vault'};
            break;
        case 'with-employee':
            if (employee) {
                location = {
                    type: 'with-employee',
                    employee: employee,
                    expTo,
                };
            }
            break;
        case 'with-guest':
            if (guestFullName && guestFullName.length > 0) {
                location = {
                    type: 'with-guest',
                    fullName: guestFullName,
                    company: guestCompany,
                    expTo,
                };
            }
            break;
        case 'lost':
            location = {type: 'lost'};
            break;
        case 'damaged':
            location = {type: 'damaged'};
            break;
        case 'deactivated':
            location = {type: 'deactivated'};
            break;
    }
    return location;
}

export function KeyLocationShield({location, tooltip, ...props}: {
    location: KeyLocation,
    tooltip?: boolean,
} & GridProps) {
    const {locationType, employee, guestFullName, guestCompany} = toLocationTypeEnum(location);

    return (
        <Grid container spacing={2} alignItems="baseline" {...props}>
            {employee ? (
                <>
                    <EmployeeAvatar employee={employee} sx={{width: 24, height: 24, alignSelf: 'center'}}/>
                    <EmployeeLink employee={employee} sx={{fontSize: 'inherit', color: tooltip ? 'white' : 'inherit'}}/>
                </>
            ) : (
                <>
                    {React.createElement(locationTypeOptions[locationType].icon, {
                        color: 'disabled',
                        fontSize: "small",
                        sx: {color: tooltip ? grey.A400 : undefined, alignSelf: 'center'}
                    })}
                    {guestFullName ? (
                        <>
                            <Typography sx={{fontSize: 'inherit'}}>
                                <Typography sx={{fontSize: 'inherit', color: 'secondary.main'}} component="span">{guestCompany && `${guestCompany}: `}</Typography>
                                <Typography sx={{fontSize: 'inherit'}} component="span">{guestFullName}</Typography>
                            </Typography>
                        </>
                    ) : (
                        <Typography sx={{fontSize: 'inherit'}}>{locationTypeOptions[locationType].label}</Typography>
                    )}
                </>
            )}
        </Grid>
    );
}

export function isEqualLocation(a: KeyLocation, b: KeyLocation) {
    if (a.type !== b.type) {
        return false;
    }

    if (a.type === 'with-employee' && b.type === 'with-employee') {
        return a.employee.id === b.employee.id && a.expTo === b.expTo;
    }

    if (a.type === 'with-guest' && b.type === 'with-guest') {
        return a.fullName === b.fullName && a.company === b.company && a.expTo === b.expTo;
    }

    return true;
}
